import * as R from 'ramda'

import type {
  Prescription,
  PrescriptionTimelineItem,
  RxCardItem,
} from '~/types'

/**
 * Picks all RxCardItem fields from a PrescriptionTimelineItem.
 * Merges PTI-level name/masterName onto prescription so card title (getPrescriptionTitle) resolves.
 */
export function toRxCardItem(item: PrescriptionTimelineItem): RxCardItem {
  const prescription = {
    ...item.prescription,
    name: item.name ?? item.prescription?.name,
    masterName: item.masterName ?? item.prescription?.masterName,
  }
  return {
    type: item.type,
    subType: item.subType,
    date: item.date,
    soapId: item.soapId,
    businessId: item.businessId,
    invoiceId: item.invoiceId,
    vet: item.vet,
    controlled: item.controlled,
    prescription,
    signerId: item.signerId,
    cancellationReasonDetail: item.cancellationReasonDetail,
  }
}

/**
 * Invoice ID for Rx card. Use in card path only; do not add to prescriptionUtils.
 */
export function getInvoiceIdFromRxCardItem(
  item: RxCardItem,
): string | null | undefined {
  return item.invoiceId ?? item.prescription?.invoiceId
}

/**
 * Converts RxCardItem to the shape expected by prescriptionUtils.timelinePrescriptionToRegularPrescription.
 */
export function convertToTimelineLikeFromRxCardItem(item: RxCardItem): {
  masterName: Prescription['masterName']
  name: Prescription['name']
  prescription: Prescription
} {
  const p = item.prescription
  return {
    name: p?.name ?? '',
    masterName: p?.masterName ?? p?.name ?? '',
    prescription: p,
  }
}

/**
 * Converts PrescriptionTimelineItem to the same timeline-like shape.
 * Used by PrescriptionsCard (old card) so prescriptionUtils is unchanged.
 */
export function convertToTimelineLikeFromPTI(item: PrescriptionTimelineItem): {
  masterName: Prescription['masterName']
  name: Prescription['name']
  prescription: Prescription
} {
  return {
    name: item.name ?? item.prescription?.name ?? '',
    masterName:
      item.masterName ?? item.prescription?.masterName ?? item.name ?? '',
    prescription: item.prescription,
  }
}

/**
 * Invoice ID for either PrescriptionTimelineItem or RxCardItem.
 * Use in shared hooks (e.g. usePrescriptionCardActions) so PrescriptionsCard can pass PTI.
 */
export function getInvoiceIdForCard(
  item: PrescriptionTimelineItem | RxCardItem,
): string | null | undefined {
  return (
    item.invoiceId ??
    item.prescription?.invoiceId ??
    R.path<string>(['invoice', 'id'], item)
  )
}

/**
 * Converts either PTI or RxCardItem to the timeline-like shape for prescriptionUtils.
 */
export function convertToTimelineLikeForPrescriptionUtils(
  item: PrescriptionTimelineItem | RxCardItem,
): {
  masterName: Prescription['masterName']
  name: Prescription['name']
  prescription: Prescription
} {
  if ('name' in item) {
    return convertToTimelineLikeFromPTI(item)
  }
  return convertToTimelineLikeFromRxCardItem(item)
}
